import {
  DEFAULT_DATA_GRID_CLASS_NAME,
  DETAILED_DATA_GRID_CLASS_NAME,
  DETAILED_SMALL_TEXT_DATA_GRID_CLASS_NAME,
  DEFAULT_FOOTER_HEIGHT,
  DIVIDER_ROW_CLASS_NAME,
  HIDE_SCROLLBAR_CLASS_NAME,
} from '../../constants/data-grid';

const defaultDataGrid = () => ({
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    border: 'none',
  },
  '& .MuiDataGrid-row': {
    backgroundColor: '#f9f9f9',
    verticalAlign: 'top',
    marginBottom: 8,
    borderRadius: 12,
  },
  '& .MuiDataGrid-cell': {
    border: 'none',
    overflow: 'auto !important',
    whiteSpace: 'initial !important',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    justifyContent: 'center',
  },
  '& .MuiDataGrid-row--disabled .disabled-row-message': {
    display: 'none',
  },
  '& .MuiDataGrid-iconSeparator': {
    zIndex: -1,
  },
});

export const detailedDataGrid = ({
  theme,
  selectedRowColor,
  selectedCheckboxColor,
  smallText = false,
}) => ({
  border: `1px solid ${theme.palette.gray.A300}`,
  borderRadius: 12,
  overflow: 'hidden',
  '& .MuiDataGrid-columnHeaders': {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    backgroundColor: theme.palette.gray.A100,
    borderBottom: `1px solid ${theme.palette.gray.A400}`,
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: 'transparent',
      '&:focus': {
        outline: 'none',
      },
      '&:focus-within': {
        outline: 'none',
      },
    },
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    color: theme.palette.gray.A800,
    fontWeight: 'bold',
    fontSize: theme.typography.large_2.fontSize,
    '& .MuiTypography-root': {
      textWrap: 'auto',
      fontSize: theme.typography.small_2.fontSize,
      fontWeight: 600,
      lineHeight: 'normal',
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.small_1.fontSize,
      },
    },
  },
  '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox': {
    '& .MuiCheckbox-root': {
      color: theme.palette.gray.A400,
      '&.Mui-checked': {
        color: selectedCheckboxColor,
      },
    },
  },
  '& .MuiDataGrid-columnHeaderCheckbox': {
    bottom: 2,
  },
  '& .MuiDataGrid-iconSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-row': {
    backgroundColor: 'white',
    verticalAlign: 'top',
    borderBottom: `1px solid ${theme.palette.gray.A300}`,
    [`&.${DIVIDER_ROW_CLASS_NAME} .MuiDataGrid-cell`]: {
      borderBottomWidth: 2,
      borderBottomStyle: 'solid',
      borderBottomColor: theme.palette.gray.A400,
    },
    '&.Mui-selected': {
      backgroundColor: selectedRowColor,
    },
    '&.Mui-selected:hover': {
      backgroundColor: selectedRowColor,
    },
  },
  '& .MuiDataGrid-cell': {
    border: 'none',
    overflow: 'auto !important',
    whiteSpace: 'initial !important',
    paddingLeft: 16,
    paddingRight: 16,
    scrollbarWidth: 'thin',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 2,
      paddingRight: 2,
      overflow: 'hidden !important',
    },
    '& .MuiTypography-root, & .MuiDataGrid-cellContent': {
      fontSize: smallText
        ? theme.typography.small_1.fontSize
        : theme.typography.small_2.fontSize,
      fontWeight: 500,
      [theme.breakpoints.down('sm')]: {
        fontSize: smallText
          ? theme.typography.xsmall_3.fontSize
          : theme.typography.small_1.fontSize,
      },
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    justifyContent: 'right',
    height: DEFAULT_FOOTER_HEIGHT,
    minHeight: DEFAULT_FOOTER_HEIGHT,
    backgroundColor: 'white',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  '& .MuiDataGrid-selectedRowCount': {
    display: 'none',
  },
  '& .MuiDataGrid-row--disabled': {
    backgroundColor: 'white',
    color: theme.palette.gray.A400,
    opacity: 0.6,
    '& .MuiDataGrid-cellCheckbox': {
      opacity: 0.6,
    },
  },
  '& .no-padding-cell': {
    paddingLeft: 0,
    paddingRight: 0,
  },
  '& .no-padding-header': {
    paddingLeft: 0,
    paddingRight: 0,
  },
  '& .first-column-header': {
    marginLeft: 8,
  },
  '& .first-column-cell': {
    marginLeft: 8,
  },
  '& .last-column-header': {
    marginRight: 8,
  },
  '& .last-column-cell': {
    marginRight: 8,
  },
  '& .icon-text-cell .MuiTypography-root': {
    fontSize: theme.typography.small_1.fontSize,
    fontWeight: theme.typography.small_1.fontWeight,
    marginTop: '6px',
  },
  '& .wrap-header-cell .MuiTypography-root': {
    whiteSpace: 'pre-line',
  },
  '& .MuiDataGrid-row:last-of-type': {
    borderBottom: 'none !important',
  },
  '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
    borderBottom: 'none !important',
  },
});

const hideScrollbar = {
  '& .MuiDataGrid-virtualScroller': {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
};
const MuiDataGrid = {
  styleOverrides: {
    root: ({ theme }) => ({
      [`&.${DEFAULT_DATA_GRID_CLASS_NAME}`]: defaultDataGrid({ theme }),
      [`&.${DETAILED_DATA_GRID_CLASS_NAME}`]: detailedDataGrid({
        theme,
        selectedRowColor: theme.palette.primary.A100,
        selectedCheckboxColor: theme.palette.primary.main,
      }),
      [`&.${DETAILED_SMALL_TEXT_DATA_GRID_CLASS_NAME}`]: detailedDataGrid({
        theme,
        selectedRowColor: theme.palette.primary.A100,
        selectedCheckboxColor: theme.palette.primary.main,
        smallText: true,
      }),
      [`&.${HIDE_SCROLLBAR_CLASS_NAME}`]: hideScrollbar,
    }),
  },
};

export default MuiDataGrid;
